import {
  CAlert,
  CButton,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from '@coreui/react';
import React, { useEffect, useState } from 'react';
import { getCancellationReasons, type CancellationReason } from '../../api/cancellationReasons';

interface ReasonModalProps {
  visible: boolean;
  action: 'cancel' | 'pause';
  loading?: boolean;
  onConfirm: (data: { reasonId: number; comment: string }) => void;
  onCancel: () => void;
}

export const ReasonModal: React.FC<ReasonModalProps> = ({
  visible,
  action,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [comment, setComment] = useState('');
  const [reasons, setReasons] = useState<CancellationReason[]>([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReasons = async () => {
      try {
        setIsLoadingReasons(true);
        setError(null);
        const data = await getCancellationReasons();
        const reasonsList = action === 'cancel' ? data.cancelPlan : data.pausePlan;
        setReasons(reasonsList.sort((a, b) => a.sortOrder - b.sortOrder));
      } catch (err) {
        setError('Failed to load reasons. Please try again.');
        console.error('Error fetching reasons:', err);
      } finally {
        setIsLoadingReasons(false);
      }
    };

    if (visible) {
      void fetchReasons();
    }
  }, [visible, action]);

  const selectedReasonData = reasons.find((r) => r.id === Number(selectedReason));

  const handleConfirm = () => {
    if (selectedReason) {
      onConfirm({ reasonId: Number(selectedReason), comment });
      // Reset form
      setSelectedReason('');
      setComment('');
    }
  };

  const handleCancel = () => {
    onCancel();
    // Reset form
    setSelectedReason('');
    setComment('');
  };

  const actionTitle = action === 'cancel' ? 'Cancel Subscription' : 'Pause Subscription';

  return (
    <CModal visible={visible} onClose={handleCancel}>
      <CModalHeader>
        <CModalTitle>{actionTitle}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {error && (
          <CAlert color="danger" className="mb-3">
            {error}
          </CAlert>
        )}
        <div className="mb-3">
          <CFormLabel htmlFor="reason">Reason for {action}:</CFormLabel>
          <CFormSelect
            id="reason"
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            disabled={loading || isLoadingReasons}
          >
            <option value="">Select a reason...</option>
            {reasons.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.displayText}
              </option>
            ))}
          </CFormSelect>
        </div>

        {selectedReasonData?.isCommentRequired === 1 && (
          <div className="mb-3">
            <CFormLabel htmlFor="comment">Please specify:</CFormLabel>
            <CFormTextarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Please provide details..."
              rows={3}
              disabled={loading}
            />
          </div>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={handleCancel} disabled={loading}>
          Cancel
        </CButton>
        <CButton
          color={action === 'cancel' ? 'danger' : 'warning'}
          onClick={handleConfirm}
          disabled={
            !selectedReason ||
            (selectedReasonData?.isCommentRequired === 1 && !comment.trim()) ||
            loading
          }
        >
          {loading ? <CSpinner size="sm" className="me-2" /> : null}
          {actionTitle}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};
